/**
 * The live scene's side of the Edit flight (`spec-onboarding-persistent-scene-edit-flight`, R4): the
 * height scale the live twin is drawn at, the floors that fade, and where the live scene is anchored.
 *
 * Held outside React, like the handover (`planHandover.ts`): the twin page starts and stops a flight,
 * and the 3D tree inside the map's canvas reads it every frame. React context does not cross the
 * react-three-map canvas, so the scene's provider (`TwinScene`) reads this module.
 *
 * At rest it is exactly the plan view's `outside` value (`PlanHeightScale.tsx`): height 1, state
 * `off`, every floor as it was. So every customer sees the same scene as before (R4.1).
 *
 * Imports no map library. Nothing here is stored, logged or sent.
 */
import type { Material, Mesh, Object3D } from 'three';
import type { PlanViewState } from './planMapLock';

/** Where the live scene is drawn: react-three-map's anchor, and the site's turn as `Site` applies it. */
export interface LiveSceneAnchor {
  origin: { latitude: number; longitude: number } | null;
  orientationDeg: number | null;
}

let heightScale = 1;
let state: PlanViewState = 'off';
const anchor: LiveSceneAnchor = { origin: null, orientationDeg: null };

/**
 * A fading floor's mesh: its original material and its see-through copies, or no copies where a
 * material cannot be copied and faded (a shader of the scene's own: the untracked hatch, the wind, the
 * heatmap). Those keep their material and are hidden half-way through the fade.
 */
interface Swap {
  mesh: Mesh;
  original: Material | Material[];
  copies: Material[] | null;
  originals: Material[];
  /** Whether the mesh was drawn before the fade: a hidden mesh stays hidden. */
  visible: boolean;
}
let swaps: Swap[] = [];
let fadeOpacity = 1;
let hiddenGroups: Object3D[] = [];

type Warm = () => void;
let warm: Warm | null = null;

/** Reads the live floors from the scene, each floor group with its BID (the bridge offers it). */
type FloorCollector = () => [string, Object3D][];
let collector: FloorCollector | null = null;

export const liveEditFlightScene = {
  getHeightScale: (): number => heightScale,
  getState: (): PlanViewState => state,
};

export const setLiveHeightScale = (k: number): void => {
  heightScale = Number.isFinite(k) ? k : 1;
};

export const setLiveSceneState = (next: PlanViewState): void => {
  state = next;
};

export const setLiveAnchorOrigin = (origin: LiveSceneAnchor['origin']): void => {
  anchor.origin = origin;
};

export const setLiveAnchorOrientation = (deg: number | null): void => {
  anchor.orientationDeg = deg;
};

export const liveAnchor = (): LiveSceneAnchor => ({ ...anchor });

/**
 * The scene offers a way to find its floors: each `Floor` group names its BID in
 * `userData.floorBid`. With none offered there are no live floors.
 */
export function registerLiveFloorCollector(fn: FloorCollector): () => void {
  collector = fn;
  return () => {
    if (collector === fn) collector = null;
  };
}

/** The live floors and their groups now, from the scene. */
export const liveFloors = (): [string, Object3D][] => collector?.() ?? [];

/** The live floors' BIDs now, for the development probe and the floor match. */
export const liveFloorBids = (): string[] => liveFloors().map(([bid]) => bid);

/** Does any of `bids` name a live floor? */
export const matchesLiveFloor = (bids: readonly string[]): boolean => {
  const live = new Set(liveFloorBids());
  return bids.some(b => live.has(b));
};

/** The scene offers a way to build every material's program now (`gl.compile`). */
export function registerLiveWarm(fn: Warm): () => void {
  warm = fn;
  return () => {
    if (warm === fn) warm = null;
  };
}

const asList = (m: Material | Material[]) => (Array.isArray(m) ? m : [m]);

/** Below this fade a mesh whose material cannot be copied is hidden. */
const HIDE_UNCOPIED_BELOW = 0.5;

/**
 * A see-through copy of `m`, or null. A shader material is never copied: its constructor may need
 * arguments `clone()` does not pass (`NotTrackedMaterial` throws), and its own shader ignores
 * `opacity`. Any other copy that throws is treated the same way.
 */
const seeThroughCopy = (m: Material): Material | null => {
  if ((m as Material & { isShaderMaterial?: boolean }).isShaderMaterial) return null;
  try {
    const copy = m.clone();
    copy.onBeforeCompile = m.onBeforeCompile;
    copy.customProgramCacheKey = m.customProgramCacheKey;
    copy.transparent = true;
    copy.opacity = m.opacity;
    return copy;
  } catch {
    return null;
  }
};

/**
 * R4.3: every live floor whose BID is not one of `keep` is made see-through, once, before the camera
 * moves: each mesh's material is swapped for a transparent copy that keeps the original's patch (the
 * machine look's normal fix), and the programs are built now, so none compiles during the flight.
 * A mesh whose material cannot be copied keeps it and is hidden half-way (`seeThroughCopy`).
 * Returns how many floors will fade. With `cut` (reduced motion), the floors are hidden instead and
 * nothing is copied.
 */
export function prepareFloorFade(keep: readonly string[], { cut = false } = {}): number {
  restoreFloors();
  const fading = liveFloors().filter(([bid]) => !keep.includes(bid));
  if (cut) {
    hiddenGroups = fading.map(([, g]) => g).filter(g => g.visible);
    hiddenGroups.forEach(g => {
      g.visible = false;
    });
    return fading.length;
  }
  fading.forEach(([, group]) => {
    group.traverse(o => {
      // Lines and points fade too: a label's pointer is a `LineSegments` (`Label3DPointer`).
      const mesh = o as Mesh;
      const drawn = o as Object3D & { isMesh?: boolean; isLine?: boolean; isPoints?: boolean };
      if (!(drawn.isMesh || drawn.isLine || drawn.isPoints) || !mesh.material) return;
      const originals = asList(mesh.material);
      const made = originals.map(seeThroughCopy);
      if (made.some(c => c === null)) {
        // One material cannot fade: the mesh keeps its own, and is hidden half-way.
        made.forEach(c => c?.dispose());
        swaps.push({
          mesh,
          original: mesh.material,
          copies: null,
          originals,
          visible: mesh.visible,
        });
        return;
      }
      const copies = made as Material[];
      swaps.push({ mesh, original: mesh.material, copies, originals, visible: mesh.visible });
      mesh.material = Array.isArray(mesh.material) ? copies : copies[0];
    });
  });
  fadeOpacity = 1;
  warm?.();
  return fading.length;
}

/**
 * The fading floors at `opacity` (1 → 0). Called each frame while they fade; it also carries the
 * machine look's metalness, the one value the look writes to its copies, onto the see-through copies.
 */
export function setFloorFade(opacity: number): void {
  fadeOpacity = Math.max(0, Math.min(1, opacity));
  swaps.forEach(({ mesh, copies, originals, visible }) => {
    if (!copies) {
      mesh.visible = visible && fadeOpacity >= HIDE_UNCOPIED_BELOW;
      return;
    }
    copies.forEach((copy, i) => {
      const original = originals[i] as Material & { metalness?: number };
      const c = copy as Material & { metalness?: number };
      if (typeof original.metalness === 'number') c.metalness = original.metalness;
      copy.opacity = original.opacity * fadeOpacity;
    });
    mesh.visible = visible && fadeOpacity > 0;
  });
}

/** Is a fade prepared (floors swapped or hidden)? */
export const floorFadePrepared = (): boolean => swaps.length > 0 || hiddenGroups.length > 0;

/** R9: every floor back as it was, at once; the copies freed. */
export function restoreFloors(): void {
  swaps.forEach(({ mesh, original, copies, visible }) => {
    mesh.material = original;
    mesh.visible = visible;
    copies?.forEach(c => c.dispose());
  });
  swaps = [];
  hiddenGroups.forEach(g => {
    g.visible = true;
  });
  hiddenGroups = [];
  fadeOpacity = 1;
}

/** R9: the scene as a customer sees it: full height, `off`, every floor restored. */
export function resetLiveScene(): void {
  heightScale = 1;
  state = 'off';
  restoreFloors();
}

/** For tests: forget everything. */
export function resetLiveEditFlightSceneForTests(): void {
  resetLiveScene();
  collector = null;
  warm = null;
  anchor.origin = null;
  anchor.orientationDeg = null;
}
